// services/websiteMessageService.ts
import { supabase } from './supabaseClient';

export interface WebsiteMessage {
  id: string;
  message_text: string;
  is_active: boolean;
  message_type: 'info' | 'welcome' | 'announcement' | 'maintenance' | 'project_release' | 'task_pending_review' | 'task_approved' | 'task_rejected' | 'survey_completed' | 'team_setup' | 'contact_form' | 'contact_reply';
  background_color: string;
  text_color: string;
  start_date: string;
  end_date?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateWebsiteMessageData {
  message_text: string;
  message_type: 'info' | 'welcome' | 'announcement' | 'maintenance' | 'project_release' | 'task_pending_review' | 'task_approved' | 'task_rejected' | 'survey_completed' | 'team_setup' | 'contact_form' | 'contact_reply';
  background_color: string;
  text_color: string;
  start_date: string;
  end_date?: string | null;
}

export interface UpdateWebsiteMessageData extends Partial<CreateWebsiteMessageData> {
  is_active?: boolean;
  message_type?: 'info' | 'welcome' | 'announcement' | 'maintenance' | 'project_release' | 'task_pending_review' | 'task_approved' | 'task_rejected' | 'survey_completed' | 'team_setup' | 'contact_form' | 'contact_reply';
}

export class WebsiteMessageService {
  // Store active subscriptions
  private static subscriptions: { [key: string]: any } = {};

  // Subscribe to real-time updates for website messages
  static subscribeToMessages(callback: (message: WebsiteMessage) => void): () => void {
    // Create a unique subscription ID
    const subscriptionId = `messages_${Date.now()}`;

    // Subscribe to INSERT events on the website_messages table
    const subscription = supabase
      .channel('website_messages_channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'website_messages'
        },
        (payload) => {
          // Check if the message is active and should be shown
          const message = payload.new as WebsiteMessage;
          const now = new Date().toISOString();

          if (
            message.is_active &&
            message.start_date <= now &&
            (!message.end_date || message.end_date >= now)
          ) {
            // Call the callback with the new message
            callback(message);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'website_messages'
        },
        (payload) => {
          // Handle updates to messages (e.g., activation/deactivation)
          const message = payload.new as WebsiteMessage;
          callback(message);
        }
      )
      .subscribe();

    // Store the subscription
    this.subscriptions[subscriptionId] = subscription;

    // Return a function to unsubscribe
    return () => {
      if (this.subscriptions[subscriptionId]) {
        supabase.removeChannel(this.subscriptions[subscriptionId]);
        delete this.subscriptions[subscriptionId];
      }
    };
  }
  // Get all messages (admin view)
  static async getAllMessages(): Promise<WebsiteMessage[]> {
    const { data, error } = await supabase
      .from('website_messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get admin-manageable messages (excludes system-generated project_release messages)
  static async getAdminManageableMessages(): Promise<WebsiteMessage[]> {
    const { data, error } = await supabase
      .from('website_messages')
      .select('*')
      .neq('message_type', 'project_release') // Exclude project release messages from admin management
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get active messages for display to users (excludes targeted messages)
  static async getActiveMessages(): Promise<WebsiteMessage[]> {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('website_messages')
      .select('*')
      .eq('is_active', true)
      .not('message_type', 'in', '(project_release,task_pending_review,task_approved,task_rejected,survey_completed,contact_form,contact_reply)') // Exclude targeted messages and contact messages
      .lte('start_date', now)
      .or(`end_date.is.null,end_date.gte.${now}`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get all active messages that haven't been dismissed by the specified user
  static async getNonDismissedActiveMessages(userId?: string): Promise<WebsiteMessage[]> {
    try {
      const allMessages = await this.getActiveMessages();
      const dismissedMessageIds = this.getDismissedMessageIds(userId);

      return allMessages.filter(message => !dismissedMessageIds.includes(message.id));
    } catch (error) {
      console.error('Error getting non-dismissed messages:', error);
      return [];
    }
  }

  // Create new message
  static async createMessage(messageData: CreateWebsiteMessageData): Promise<string> {
    try {
      console.log('Creating message with data:', messageData);

      // Simplify the data structure to minimize potential issues
      const dataToInsert = {
        message_text: messageData.message_text,
        message_type: messageData.message_type,
        background_color: messageData.background_color || '#e3f2fd',
        text_color: messageData.text_color || '#1976d2',
        start_date: messageData.start_date || new Date().toISOString(),
        end_date: messageData.end_date || null,
        is_active: true
      };

      const { data, error } = await supabase
        .from('website_messages')
        .insert(dataToInsert)
        .select('id')
        .single();

      if (error) {
        console.error('Error creating message:', error);
        throw new Error(`Failed to create message: ${error.message}`);
      }

      console.log('Message created successfully:', data);
      return data.id;
    } catch (error: any) {
      console.error('Error in createMessage:', error);
      throw new Error(`Failed to create message: ${error.message}`);
    }
  }

  // Update message
  static async updateMessage(id: string, updateData: UpdateWebsiteMessageData): Promise<void> {
    const { error } = await supabase
      .from('website_messages')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Delete message
  static async deleteMessage(id: string): Promise<void> {
    const { error } = await supabase
      .from('website_messages')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Toggle message active status
  static async toggleMessageStatus(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('website_messages')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Get message by ID
  static async getMessage(id: string): Promise<WebsiteMessage | null> {
    const { data, error } = await supabase
      .from('website_messages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  // Get current active message to display (prioritizes the most recent one, excludes targeted messages)
  static async getCurrentMessage(userId?: string): Promise<WebsiteMessage | null> {
    try {
      const now = new Date().toISOString();

      // Get all active global messages first (exclude targeted messages)
      const { data: allMessages, error } = await supabase
        .from('website_messages')
        .select('*')
        .eq('is_active', true)
        .not('message_type', 'in', '(project_release,task_pending_review,task_approved,task_rejected,survey_completed,contact_form,contact_reply)') // Exclude targeted messages and contact messages
        .lte('start_date', now)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching current message:', error);
        return null;
      }

      if (!allMessages || allMessages.length === 0) {
        return null;
      }

      // Get dismissed message IDs from localStorage for the specified user
      const dismissedMessageIds = this.getDismissedMessageIds(userId);

      // Find the first message that hasn't been dismissed
      const firstNonDismissedMessage = allMessages.find(
        message => !dismissedMessageIds.includes(message.id)
      );

      // If all messages have been dismissed, return the most recent one
      return firstNonDismissedMessage || allMessages[0];
    } catch (error) {
      console.error('Unexpected error in getCurrentMessage:', error);
      return null;
    }
  }

  // Get dismissed message IDs from localStorage for a specific user
  static getDismissedMessageIds(userId?: string): string[] {
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);

      // Filter keys that match the dismissed message pattern for this user
      const pattern = userId ? `dismissed_message_${userId}_` : 'dismissed_message_';
      const dismissedKeys = keys.filter(key => key.startsWith(pattern));

      // Extract message IDs from keys
      if (userId) {
        return dismissedKeys.map(key => key.replace(`dismissed_message_${userId}_`, ''));
      } else {
        // Legacy support for old format
        return dismissedKeys.map(key => key.replace('dismissed_message_', ''));
      }
    } catch (error) {
      console.error('Error getting dismissed message IDs:', error);
      return [];
    }
  }

  // Clear all dismissed messages from localStorage
  static clearAllDismissedMessages(): void {
    try {
      const dismissedKeys = Object.keys(localStorage)
        .filter(key => key.startsWith('dismissed_message_'));

      dismissedKeys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing dismissed messages:', error);
    }
  }
}